/** Leaflet on demand, from the same CDN the Jinja screens used, so the bundle
 * carries no map library for screens without a map. Typed to what the
 * planner uses and nothing more. */

export interface LLayer {
  addTo: (m: unknown) => LLayer;
  bindPopup: (h: string) => LLayer;
  remove: () => void;
}

export interface LLayerGroup {
  addTo: (m: LMap) => LLayerGroup;
  clearLayers: () => void;
}

export interface LMap {
  setView: (c: [number, number], z: number) => LMap;
  fitBounds: (b: unknown, opts?: object) => LMap;
  on: (event: string, handler: (e: { latlng: { lat: number; lng: number }; name?: string }) => void) => void;
  remove: () => void;
  removeLayer: (layer: unknown) => void;
}

export interface Leaflet {
  map: (el: HTMLElement, opts?: object) => LMap;
  tileLayer: (url: string, opts: object) => LLayer;
  layerGroup: () => LLayerGroup;
  marker: (c: [number, number], opts?: object) => LLayer;
  circleMarker: (c: [number, number], opts: object) => LLayer;
  polyline: (points: [number, number][], opts: object) => LLayer;
  divIcon: (opts: object) => unknown;
  latLngBounds: (points: [number, number][]) => { pad: (n: number) => unknown };
  control: { layers: (bases: object, overlays: object, opts: object) => LLayer };
}

let loading: Promise<Leaflet> | null = null;

export function loadLeaflet(): Promise<Leaflet> {
  const found = (window as unknown as { L?: Leaflet }).L;
  if (found) {
    return Promise.resolve(found);
  }
  if (loading === null) {
    loading = new Promise<Leaflet>((resolve, reject) => {
      const css = document.createElement("link");
      css.rel = "stylesheet";
      css.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.append(css);
      const script = document.createElement("script");
      script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
      script.onload = () => {
        const L = (window as unknown as { L?: Leaflet }).L;
        if (L) {
          resolve(L);
        } else {
          reject(new Error("Leaflet did not load"));
        }
      };
      script.onerror = () => reject(new Error("Leaflet could not be fetched"));
      document.head.append(script);
    });
  }
  return loading;
}

export const STREET_TILES = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
export const SATELLITE_TILES = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";

/** A numbered pin, as the planner drew it. */
export function pin(L: Leaflet, color: string, text: string, size: number): unknown {
  return L.divIcon({
    className: "route-pin",
    html: `<span style="background:${color};width:${size}px;height:${size}px;line-height:${size}px">${text}</span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}
